import { Link } from 'react-router-dom';
import { Carte, Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { useAuth } from '../../auth/useAuth';
import { useIntegration } from '../../lib/useIntegration';
import { ErreurApi } from '../../lib/api';

interface IntegrationAlertsProps {
  siteId?: string | null;
}

const MAX_LIGNES = 5;

/**
 * Encart « Intégration imminente » du tableau de bord pédagogique : les entrées
 * en formation qui tombent dans le préavis (huit jours), servies par la même
 * source que l'écran `ped-integration`. L'encart n'apparaît pas pour un profil
 * qui n'a pas cet écran : l'API le refuserait, autant ne pas l'interroger.
 */
export function IntegrationAlerts({ siteId }: IntegrationAlertsProps): JSX.Element | null {
  const { peutAcceder } = useAuth();
  if (!peutAcceder('ped-integration')) return null;
  return <EncartIntegration siteId={siteId} />;
}

function EncartIntegration({ siteId }: IntegrationAlertsProps): JSX.Element {
  const { data, isLoading, error } = useIntegration(siteId || 'TOUS', true);
  const preavis = data?.compteurs.preavisJours ?? 8;
  const entrees = data?.entrees ?? [];

  return (
    <Carte titre={`Intégration imminente (${preavis}j)`} sansBordure>
      {isLoading && <Chargement libelle="Chargement des intégrations…" />}

      {error && (
        <div className="p-4">
          <MessageErreur
            message={
              error instanceof ErreurApi
                ? error.message
                : 'Les intégrations imminentes n’ont pas pu être chargées.'
            }
          />
        </div>
      )}

      {!isLoading && !error && entrees.length === 0 && (
        <p className="p-4 text-sm text-codialis-gray">Aucune intégration imminente.</p>
      )}

      {!isLoading && !error && entrees.length > 0 && (
        <>
          <div className="divide-y divide-gray-100">
            {entrees.slice(0, MAX_LIGNES).map((entree) => (
              <div
                key={entree.apprenantId}
                className="flex flex-wrap items-center justify-between gap-2 p-4"
              >
                <div className="min-w-0">
                  <div className="truncate font-semibold text-codialis-navy">
                    {entree.apprenant}
                  </div>
                  <div className="text-sm text-codialis-gray">
                    Prévue le {entree.dateEntreeLisible} · {entree.decompte}
                  </div>
                </div>
                <Pastille texte={entree.etatLibelle} ton={entree.etatTon} />
              </div>
            ))}
          </div>
          <div className="border-t border-gray-100 p-3 text-center">
            <Link
              to="/ped-integration"
              className="text-xs font-semibold text-codialis-sky hover:underline"
            >
              {entrees.length > MAX_LIGNES
                ? `Voir les ${entrees.length} intégrations`
                : 'Préparer les convocations'}
            </Link>
          </div>
        </>
      )}
    </Carte>
  );
}
